import { UAParser } from 'ua-parser-js';
import type Player from 'video.js/dist/types/player';
import { vimondAnalytics } from './VimondAnalytics';

type PlayerWithQualityLevels = Player & {
  qualityLevels?: () => {
    selectedIndex: number;
    levels_: {
      bitrate: number;
      height: number;
      id: string;
      label: string;
      width: number;
    }[];
    on: (event: string, handler: () => void) => void;
    off: (event: string, handler: () => void) => void;
  };
  playback?: {
    isLive: boolean;
    assetTitle: string;
    assetId: string;
    recommendedStream?: {
      manifest?: { uri: string };
      drmProxyType?: string;
    };
    categoryId?: string;
  };
  videoKey?: string;
};

export type VideoJsAnalyticsConfig = {
  player: Player;
  playback: {
    assetId: string;
    assetTitle: string;
    isLive: boolean;
    isDrmProtected: boolean;
    recommendedStream?: {
      manifest?: { uri: string };
      drmProxyType?: string;
    };
    categoryId?: string;
  };
  videoKey: string;
  eventsinkUrl: string;
  sessionId?: string;
  heartbeatInterval?: number;
  debug?: boolean;
  enableAdEvents?: boolean;
  adProfileId?: string;
  shardId?: string;
  additionalMetadata?: Record<string, unknown>;
};

export const VimondAnalyticsIntegration = (config: VideoJsAnalyticsConfig) => {
  const player = config.player as PlayerWithQualityLevels;
  const analytics = vimondAnalytics({
    eventsinkUrl: config.eventsinkUrl,
    sessionId: config.sessionId,
    heartbeatInterval: config.heartbeatInterval,
    debug: config.debug,
    enableAdEvents: config.enableAdEvents,
    adProfileId: config.adProfileId,
    shardId: config.shardId,
    player: {
      muted: () => player.muted?.() ?? false,
      autoplay: () => {
        const autoplayValue = player.autoplay?.();
        // Convert string values like 'muted', 'play', 'any' to boolean
        return Boolean(autoplayValue);
      },
    },
  });
  let qualityChangeHandler: (() => void) | undefined;
  let qualityLevelsRef:
    | {
        selectedIndex: number;
        levels_: {
          bitrate: number;
          height: number;
          id: string;
          label: string;
          width: number;
        }[];
        on: (event: string, handler: () => void) => void;
        off: (event: string, handler: () => void) => void;
      }
    | undefined;

  const init = async () => {
    await analytics.init();

    const videoElement = player.el().querySelector('video');
    if (videoElement instanceof HTMLVideoElement) {
      analytics.load(videoElement);
    }

    setupQualityLevelMonitoring();
    reportInitialMetadata();
  };

  const setupQualityLevelMonitoring = () => {
    if (!player.qualityLevels) return;

    const qualityLevels = player.qualityLevels();
    qualityLevelsRef = qualityLevels;
    qualityChangeHandler = () => {
      const currentQualityIndex = qualityLevels.selectedIndex;
      const selectedLevel = qualityLevels.levels_[currentQualityIndex];
      const bitrate = selectedLevel?.bitrate ?? 0;
      analytics.reportBitrateChange(bitrate);
    };
    qualityLevels.on('change', qualityChangeHandler);
  };

  // Web SDK initialization
  const reportInitialMetadata = () => {
    const parser = new UAParser(navigator.userAgent);
    const result = parser.getResult();
    const ua = navigator.userAgent;
    const normalizedDeviceType = (result.device.type ?? '').toLowerCase();

    const deviceType =
      normalizedDeviceType === 'tablet'
        ? 'Tablet'
        : normalizedDeviceType === 'mobile'
          ? 'Mobile'
          : /iPad|Tablet/i.test(ua)
            ? 'Tablet'
            : /Mobile|Android|iPhone/i.test(ua)
              ? 'Mobile'
              : 'Desktop';

    const baseMetadata = {
      live: config?.playback?.isLive ?? false,
      contentTitle: config?.playback?.assetTitle ?? '',
      contentId: config?.playback?.assetId ?? '',
      contentUrl: config?.playback?.recommendedStream?.manifest?.uri ?? '',
      drmType: config?.playback?.isDrmProtected ? 'drm' : 'none',
      deviceId: `${result.browser.name}`,
      deviceModel: `${result.os.name}`,
      deviceType,
      userId: config.videoKey ?? '',
      categoryId: config?.playback?.categoryId,
    };

    // Merge additional metadata if provided
    const finalMetadata = config.additionalMetadata
      ? { ...baseMetadata, ...config.additionalMetadata }
      : baseMetadata;

    analytics.reportMetadata(finalMetadata);
  };

  const reportAdEvent = (payload: {
    data: unknown;
    adState: string;
    adPosition?: 'pre' | 'mid' | 'post';
    metadata?: Record<string, unknown>;
  }) => {
    analytics.reportAdEvent(payload);
  };

  const reportMetadata = (metadata: Record<string, unknown>) => {
    analytics.reportMetadata(metadata);
  };

  const deinit = () => {
    if (qualityChangeHandler && qualityLevelsRef) {
      qualityLevelsRef.off('change', qualityChangeHandler);
    }
    analytics.deinit();
  };

  return {
    init,
    deinit,
    reportAdEvent,
    reportMetadata,
    getAnalyticsInstance: () => analytics,
  };
};
